import React, { useEffect, useState, useCallback } from 'react';
import { common } from '@constants';
import BookingWidgetView from '@components/views/BookingWidget';

const STEPS = { DATE: 'date', TIME: 'time', DETAILS: 'details', CONFIRM: 'confirm' };

const today = () => new Date().toISOString().slice(0, 10);
const addDays = (iso, n) => {
  const d = new Date(iso + 'T12:00:00');
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
};

const BookingWidget = () => {
  const slug = new URLSearchParams(window.location.search).get('slug') || 'bored-brewing';

  const [config, setConfig] = useState(null);
  const [step, setStep] = useState(STEPS.DATE);
  const [date, setDate] = useState(today());
  const [partySize, setPartySize] = useState(2);
  const [slots, setSlots] = useState([]);
  const [selectedTime, setSelectedTime] = useState(null);
  const [selectedPeriodId, setSelectedPeriodId] = useState(null);
  const [form, setForm] = useState({ first_name: '', last_name: '', phone: '', email: '', notes: '' });
  const [loading, setLoading] = useState(false);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [confirmation, setConfirmation] = useState(null);
  const [error, setError] = useState(null);

  // Fetch tenant config
  useEffect(() => {
    fetch(`${common.BASE_URL}/booking/config?slug=${slug}`)
      .then(r => r.json())
      .then(data => setConfig(data))
      .catch(() => setConfig({ name: 'Book a Table', brandColors: { primary: '#111' } }));
  }, [slug]);

  // Fetch availability when date or party size changes
  const fetchSlots = useCallback(async (d, p) => {
    setSlotsLoading(true);
    setSlots([]);
    setSelectedTime(null);
    try {
      const r = await fetch(`${common.BASE_URL}/booking/availability?slug=${slug}&date=${d}&party_size=${p}`);
      const data = await r.json();
      setSlots(Array.isArray(data) ? data : []);
    } catch {
      setSlots([]);
    } finally {
      setSlotsLoading(false);
    }
  }, [slug]);

  const handleDateNext = useCallback(() => {
    fetchSlots(date, partySize);
    setStep(STEPS.TIME);
  }, [date, partySize, fetchSlots]);

  const handleTimeNext = useCallback((time, periodId) => {
    setSelectedTime(time);
    setSelectedPeriodId(periodId || null);
    setStep(STEPS.DETAILS);
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!form.first_name || !form.last_name || (!form.phone && !form.email)) {
      setError('Please provide your name and either a phone number or email.');
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const res = await fetch(`${common.BASE_URL}/booking/reserve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          slug,
          ...form,
          party_size: partySize,
          date,
          time: selectedTime,
          service_period_id: selectedPeriodId,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Booking failed');
      setConfirmation(data);
      setStep(STEPS.CONFIRM);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [form, slug, partySize, date, selectedTime, selectedPeriodId]);

  const handleReset = useCallback(() => {
    setStep(STEPS.DATE);
    setDate(today());
    setPartySize(2);
    setSlots([]);
    setSelectedTime(null);
    setForm({ first_name: '', last_name: '', phone: '', email: '', notes: '' });
    setConfirmation(null);
    setError(null);
  }, []);

  return (
    <BookingWidgetView
      config={config}
      step={step}
      date={date}
      partySize={partySize}
      slots={slots}
      selectedTime={selectedTime}
      form={form}
      loading={loading}
      slotsLoading={slotsLoading}
      confirmation={confirmation}
      error={error}
      addDays={addDays}
      onDateChange={setDate}
      onPartySizeChange={setPartySize}
      onDateNext={handleDateNext}
      onBack={() => setStep(step === STEPS.DETAILS ? STEPS.TIME : STEPS.DATE)}
      onTimeSelect={handleTimeNext}
      onFormChange={(field, val) => setForm(prev => ({ ...prev, [field]: val }))}
      onSubmit={handleSubmit}
      onReset={handleReset}
    />
  );
};

export default BookingWidget;
